const CreditCardFormComponent = require('app/scripts/views/billing/credit-card-form-component.coffee');
const PaidAccount = require('app/scripts/network/paid-account.coffee');
const AjaxPromise = require('app/scripts/network/ajax-promise.coffee');
const Auth = require('app/scripts/db/auth.coffee');

import { useFeatureFlag } from '@trello/feature-flag-client';
import experiment from 'app/gamma/src/util/experiments';
import React, { useState } from 'react';
import styles from './MoonshotBilling.less';
import classNames from 'classnames';
import { logoDomain } from '@trello/config';
import {
  useMoonshotBillingQuery,
  useMoonshotCampaignQuery,
  useMoonshotUpdateCampaignMutation,
} from '@trello/graphql/generated';
import {
  BillingDates,
  OrganizationFeatures as OrgFeatures,
} from '@trello/product-features';
import { Icon, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { deserializeJSONString } from '@trello/graphql';
import { forNamespace } from 'app/common/lib/util/i18n';
import { Spinner } from 'app/src/components/Spinner';

const format = forNamespace(['moonshot', 'billing']);

interface MoonshotBillingProps {
  onFailure: () => void;
  onSkip: () => void;
  onSuccess: () => void;
}

export const MoonshotBilling: React.FC<MoonshotBillingProps> = ({
  onFailure,
  onSkip,
  onSuccess,
}) => {
  const [updateCampaign] = useMoonshotUpdateCampaignMutation();
  const urlParams = new URLSearchParams(window.location.search);
  const slackPromoCode = urlParams.get('promocode');
  const [isBillingAuthorising, setIsBillingAuthorising] = useState(false);
  const isFreeTrialExperiment =
    useFeatureFlag('btg.bc-free-trial', false) &&
    // We don't want users that are signing up with Slack promocode get bucketed into the Free trial experiment.
    // Once the get the Experiment code runs, it will have a side effect of bucketing users to it.
    // However, we still want to use the Moonshot flow.
    !slackPromoCode &&
    experiment.get('moonshotDirectSignup').get('variation') === 'enabled';
  const { data, loading } = useMoonshotBillingQuery();
  const org =
    data &&
    data.member &&
    data.member.organizations &&
    // get the oldest org that a user is an admin of
    data.member.organizations.find((team) => {
      const myMembership = team?.memberships?.find((membership) => {
        return membership.idMember === data?.member?.id;
      });

      return myMembership?.memberType === 'admin';
    });

  const invitedUsersCount =
    org &&
    org.memberships &&
    org.memberships.reduce(
      (count, membership) => (membership.unconfirmed ? count + 1 : count),
      0,
    );

  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);
  const orgId = (org && org.id) || '';
  const orgDisplayName = (org && org.displayName) || '';
  const orgLogoHash = (org && org.logoHash) || '';
  const orgImageUrl = [logoDomain, orgLogoHash, '170.png'].join('/');
  const activeBillableMemberCount = (org && org.activeBillableMemberCount) || 0;
  const billableCollaboratorCount = (org && org.billableCollaboratorCount) || 0;
  const billableMemberCount = (org && org.billableMemberCount) || 0;
  const paidAccount = org && org.paidAccount;

  // We need to carefully deserialize the JSONString for billing dates that is stored in the
  // Apollo Cache
  const billingDates =
    deserializeJSONString<BillingDates>(paidAccount?.billingDates) ?? {};
  const paidAccountPollURL = `/1/organizations/${orgId}?paid_account=true`;
  const createAccountData = {
    id: orgId,
    urlRoot: '/1/organizations',
  };

  const { yearly, monthly, billingStrategy } = OrgFeatures.currentPriceObject(
    activeBillableMemberCount,
    billableCollaboratorCount,
    billableMemberCount,
    billingDates,
  );

  const { data: campaignData } = useMoonshotCampaignQuery();
  const slackCodesCampaign = campaignData?.member?.campaigns?.find(
    (campaign) => campaign.name === 'SlackDiscount',
  );

  const hasPromo =
    useFeatureFlag('btg.slack-codes', false) &&
    (slackPromoCode || !slackCodesCampaign?.dateDismissed);

  const onSkipWithTracking = () => {
    onSkip();
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SKIPS,
      directObj: Noun.BUSINESS_CLASS_PAYMENT,
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'skipped',
        actionSubject: 'payment',
        actionSubjectId: 'bcPayment',
        source: 'moonshotPaymentScreen',
      });
    }

    if (slackCodesCampaign) {
      // no need to block on this, so no await
      updateCampaign({
        variables: {
          id: slackCodesCampaign.id,
          isDismissed: true,
        },
      });
    }
  };

  const creditCardFormProps = {
    hasPromo,
    promocode: slackPromoCode,
    isMoonshotBilling: true,
    isFreeTrial: isFreeTrialExperiment,
    defaultProduct: yearly.productNumber,
    monthlyProduct: monthly,
    yearlyProduct: yearly,
    billingStrategy: billingStrategy,
    creditCardFormTitle: isFreeTrialExperiment ? (
      <h3 className={styles.creditCardFormTitle}>
        {format('free trial credit card form heading part 1')}{' '}
        <span>{format('free trial credit card form heading part 2')}</span>
      </h3>
    ) : null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit: (submitData: any) => {
      setIsBillingAuthorising(true);

      if (isFreeTrialExperiment) {
        trackUe({
          category: Noun.MOONSHOT,
          verb: Verb.ACCEPTS,
          directObj: Noun.BUSINESS_CLASS_FREE_TRIAL,
        });

        if (sendGASEvent) {
          Analytics.sendTrackEvent({
            action: 'accepted',
            actionSubject: 'trial',
            actionSubjectId: 'bcFreeTrial',
            source: 'moonshotPaymentScreen',
          });
        }
      } else {
        trackUe({
          category: Noun.MOONSHOT,
          verb: Verb.SUBMITS,
          directObj: Noun.BUSINESS_CLASS_PAYMENT,
        });

        if (sendGASEvent) {
          Analytics.sendTrackEvent({
            action: 'submitted',
            actionSubject: 'payment',
            actionSubjectId: 'bcPayment',
            source: 'moonshotPaymentScreen',
          });
        }
      }

      if (isFreeTrialExperiment) {
        submitData.freeTrial = true;
      }

      return PaidAccount.createAccount(createAccountData, submitData);
    },
    doPoll: () => AjaxPromise({ url: paidAccountPollURL }),
    buttonText: isFreeTrialExperiment
      ? format('free trial billing submit button')
      : format('business class billing submit button'),
    extraData: {
      billingContactFullName:
        (paidAccount && paidAccount.contactFullName) ||
        Auth.me().get('fullName'),
      billingContactEmail:
        (paidAccount && paidAccount.contactEmail) || Auth.me().get('email'),
      billingContactLocale:
        (paidAccount && paidAccount.contactLocale) ||
        Auth.me().getPref('locale'),
    },
    // Called on error of credit card
    onCreditCardError: () => {
      setIsBillingAuthorising(false);
      return onFailure();
    },
    // Called on error of form submission
    didntGetAuthStatus: () => {
      setIsBillingAuthorising(false);
      return onFailure();
    },
    // Called on success of form submission - should eventually redirect user to success page
    didGetAuthStatus: () => {
      if (slackCodesCampaign) {
        // no need to block on this, so no await
        updateCampaign({
          variables: {
            id: slackCodesCampaign.id,
            isDismissed: true,
          },
        });
      }

      onSuccess();
    },
  };

  if (loading) {
    return <Spinner centered />;
  }

  return (
    <>
      <div
        className={classNames(styles.moonshotBillingWrapper, 'billing', {
          [styles.moonshotBillingFreeTrial]: isFreeTrialExperiment,
        })}
      >
        <div className={styles.moonshotBillingHeading}>
          {isFreeTrialExperiment ? (
            <>
              <h1>
                <img
                  src={require('resources/images/moonshot/bc-sparkle.svg')}
                  alt=""
                />
                {format('free trial heading')}
              </h1>
              <p>{format('free trial subheading')}</p>
            </>
          ) : (
            <>
              <div className={styles.moonshotBillingOrgImage}>
                {orgLogoHash ? (
                  <img src={orgImageUrl} alt={orgDisplayName} />
                ) : (
                  <Icon
                    name={IconName.Organization}
                    className={styles.moonshotBillingOrgIcon}
                  />
                )}
              </div>
              <h1 className={styles.moonshotBillingOrgName}>
                {orgDisplayName}
              </h1>
            </>
          )}
        </div>
        <CreditCardFormComponent
          invitedUsersCount={invitedUsersCount}
          {...creditCardFormProps}
        />
        {!isFreeTrialExperiment && (
          <>
            <div className={styles.moonshotBillingCancellationCopy}>
              {format('cancellation messaging')}
            </div>
            <div className={styles.moonshotBillingSkip}>
              <div className={styles.moonshotBillingSkipHeading}>
                {format('skip business class heading')}
              </div>
              <div className={styles.moonshotBillingSkipDescription}>
                {format('skip business class subheading')}
              </div>
              <div
                className={styles.moonshotBillingSkipCTA}
                onClick={onSkipWithTracking}
                role="link"
                data-test-id="billing-skip-cta"
              >
                {format('skip business class button')}
              </div>
            </div>
          </>
        )}
      </div>
      {isFreeTrialExperiment && (
        <button
          className={styles.startFreeButton}
          onClick={onSkipWithTracking}
          disabled={isBillingAuthorising}
          data-test-id="billing-skip-cta"
        >
          {format('skip free trial button')}
        </button>
      )}
    </>
  );
};
